import React from "react";
import { getDiffDaysMessage } from "../../../utils/main";

export default function ProcedureTimeline({
  indexEncounter,
}: {
  indexEncounter: EncounterSearchResult;
}) {
  const noDataAvailable = "no data available";
  let admitDiffDays = noDataAvailable;
  let dischargeDiffDays = noDataAvailable;
  let lengthOfStay = noDataAvailable;
  let operationRoomEntered = noDataAvailable;
  let operationRoomExited = noDataAvailable;
  let operationRoomDuration = noDataAvailable;
  let operationStarted = noDataAvailable;
  let operationEnded = noDataAvailable;
  let operationDuration = noDataAvailable;

  if (indexEncounter) {
    if (Number.isInteger(indexEncounter.diffDaysAdmitted)) {
      admitDiffDays = getDiffDaysMessage(indexEncounter.diffDaysAdmitted);
    }
    if (Number.isInteger(indexEncounter.diffDaysDischarged)) {
      dischargeDiffDays = getDiffDaysMessage(indexEncounter.diffDaysDischarged);
    }
    if (
      indexEncounter.diffDaysAdmitted !== null &&
      indexEncounter.diffDaysDischarged !== null &&
      Number.isInteger(indexEncounter.diffDaysAdmitted) &&
      Number.isInteger(indexEncounter.diffDaysDischarged)
    ) {
      const losDays =
        indexEncounter.diffDaysDischarged - indexEncounter.diffDaysAdmitted;
      if (losDays === 0) {
        lengthOfStay = getDiffDaysMessage(losDays);
      } else {
        lengthOfStay = losDays + " d";
      }
    }
    if (indexEncounter.operationRoomEntered) {
      operationRoomEntered = indexEncounter.operationRoomEntered;
    }
    if (indexEncounter.operationRoomExited) {
      operationRoomExited = indexEncounter.operationRoomExited;
    }
    if (indexEncounter.operationStarted) {
      operationStarted = indexEncounter.operationStarted;
    }
    if (indexEncounter.operationEnded) {
      operationEnded = indexEncounter.operationEnded;
    }
    if (indexEncounter.operationDuration) {
      operationDuration = indexEncounter.operationDuration;
    }
    if (indexEncounter.operationRoomDuration) {
      operationRoomDuration = indexEncounter.operationRoomDuration;
    }
  }

  const timelineData: TimelineItem[] = [
    { id: "admitted", label: "Admitted", time: admitDiffDays },
    { id: "entered", label: "Entered Room", time: operationRoomEntered },
    { id: "started", label: "Started Procedure", time: operationStarted },
    { id: "ended", label: "Ended Procedure", time: operationEnded },
    { id: "exited", label: "Exited Room", time: operationRoomExited },
    { id: "discharged", label: "Discharged", time: dischargeDiffDays },
  ];

  const durationData: DurationItem[] = [
    {
      startId: "started",
      endId: "ended",
      label: "Procedure Duration",
      value: operationDuration,
      colorName: "gray",
      level: 1,
    },
    {
      startId: "entered",
      endId: "exited",
      label: "Patient in Room",
      value: operationRoomDuration,
      colorName: "green",
      level: 2,
    },
    {
      startId: "admitted",
      endId: "discharged",
      label: "Hospital Stay",
      value: lengthOfStay,
      colorName: "blue",
      level: 3,
    },
  ];

  // Define the props interface for the DurationPath component
  interface DurationPathProps {
    startY: number;
    endY: number;
    level: number;
    className: string;
  }

  const DurationPath: React.FC<DurationPathProps> = ({
    startY,
    endY,
    level,
    className,
  }) => {
    const dotX = 150;
    const levelX = level * 70 + 80;
    const radius = 10;

    const pathData = `
      M ${dotX},${startY}
      h ${levelX - radius}
      a ${radius},${radius} 0 0 1 ${radius},${radius}
      v ${endY - startY - 2 * radius}
      a ${radius},${radius} 0 0 1 -${radius},${radius}
      H ${dotX + 20}
    `;

    return (
      <path
        d={pathData}
        fill="none"
        strokeWidth="1"
        className={className}
        markerEnd="url(#arrowhead)"
      />
    );
  };

  // Specify the types for the Map's key and value
  const itemPositions = new Map<string, number>();
  timelineData.forEach((item, index) => {
    itemPositions.set(item.id, index * 80 + 15);
  });

  return (
    <>
      <div className="timeline-section">
        <h2>Procedure Timeline</h2>
        <div className="timeline-body">
          <div className="vertical-dotted-line"></div>
          <svg className="svg-container">
            <defs>
              <marker
                id="arrowhead"
                viewBox="0 0 10 10"
                refX="5"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="black" />
              </marker>
            </defs>
            {durationData.map((duration) => {
              const startY = itemPositions.get(duration.startId)!;
              const endY = itemPositions.get(duration.endId)!;
              const levelX = duration.level * 70 + 175;
              const levelY = duration.level * 75 + 115;

              return (
                <g key={duration.label}>
                  <DurationPath
                    startY={startY}
                    endY={endY}
                    level={duration.level}
                    className={`svg-path ${duration.colorName}`}
                  />
                  <foreignObject x={levelX} y={levelY} width="100" height="50">
                    <div
                      className={`duration-label-group ${duration.colorName}`}
                    >
                      <div className="duration-label">{duration.label}</div>
                      <div className="duration-value">{duration.value}</div>
                    </div>
                  </foreignObject>
                </g>
              );
            })}
          </svg>
          {timelineData.map((item) => (
            <div
              key={item.id}
              className="timeline-event"
              style={{ height: "40px" }}
            >
              <div className="timeline-dot-wrapper">
                <div className="timeline-dot gray"></div>
              </div>
              <div className="event-label-time">
                <p className="label">{item.label}</p>
                <p className="time">{item.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
